import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertTriangle, CheckSquare, ChevronDown, ExternalLink, Info, Plus, ShieldCheck, X } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { ClearanceAiPromptSection } from './ClearanceAiPromptSection.tsx'
import {
  Button,
  Card,
  Dropdown,
  DropdownCheckboxItem,
  DropdownContent,
  DropdownTrigger,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  PageHeader,
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  useToast,
} from '@/components/ui'

interface TicketTagRule {
  id: string
  tagIds: number[]
  minimumDaysSinceUpdate: number
}

interface FreigabeSettings {
  closedTicketsEnabled: boolean
  closedTicketsDays: number
  ticketTagRulesEnabled: boolean
  ticketTagRules: TicketTagRule[]
  criteriaLogic: 'AND' | 'OR'
  checkTimeDeviation: boolean
  checkTimeDeviationPercent: number
  checkMissingMaterial: boolean
  checkBillability: boolean
  checkAiPlausibility: boolean
}

interface TanssTag {
  id: string
  name: string
  isGroupParent: boolean
  groupTagId: number
}

const DEFAULTS: FreigabeSettings = {
  closedTicketsEnabled: false,
  closedTicketsDays: 3,
  ticketTagRulesEnabled: false,
  ticketTagRules: [],
  criteriaLogic: 'OR',
  checkTimeDeviation: true,
  checkTimeDeviationPercent: 20,
  checkMissingMaterial: false,
  checkBillability: false,
  checkAiPlausibility: false,
}

function extractErrorMessage(err: unknown): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    (err as Error)?.message ??
    String(err)
  )
}

function TagMultiSelect({
  tagIds,
  onChange,
  tags,
}: {
  tagIds: number[]
  onChange: (ids: number[]) => void
  tags: TanssTag[]
}) {
  const [search, setSearch] = useState('')

  const leafTags = tags.filter((t) => !t.isGroupParent)
  const filtered = leafTags.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))

  const toggle = (id: number) => {
    onChange(tagIds.includes(id) ? tagIds.filter((i) => i !== id) : [...tagIds, id])
  }

  const selectedNames = tagIds.map((id) => {
    const t = tags.find((tt) => tt.id === String(id))
    return t?.name ?? `#${id}`
  })

  return (
    <Dropdown>
      <DropdownTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 w-full text-left min-h-[38px]"
        >
          <span className="flex-1 truncate">
            {tagIds.length === 0
              ? <span className="text-gray-400">Tags auswählen…</span>
              : selectedNames.join(', ')
            }
          </span>
          <ChevronDown className="w-4 h-4 shrink-0 text-gray-400" />
        </button>
      </DropdownTrigger>
      <DropdownContent align="start" className="w-72">
        <div className="p-2 border-b border-gray-100">
          <Input
            placeholder="Tag suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div className="max-h-56 overflow-y-auto">
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-caption text-gray-400">Keine Tags gefunden</p>
          )}
          {filtered.map((tag) => {
            const parent = tags.find((p) => p.isGroupParent && p.id === String(tag.groupTagId))
            return (
              <DropdownCheckboxItem
                key={tag.id}
                checked={tagIds.includes(Number(tag.id))}
                onCheckedChange={() => toggle(Number(tag.id))}
                onSelect={(e) => e.preventDefault()}
              >
                {parent ? `${parent.name} / ${tag.name}` : tag.name}
              </DropdownCheckboxItem>
            )
          })}
        </div>
      </DropdownContent>
    </Dropdown>
  )
}

export function DispoFreigabeSettingsPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [form, setForm] = useState<FreigabeSettings>(DEFAULTS)

  const { data, isLoading, error } = useQuery<FreigabeSettings>({
    queryKey: ['dispo-freigabe-settings'],
    queryFn: async () => {
      const res = await apiClient.get('/dispo/freigabe-settings')
      return res.data
    },
  })

  const tagsQuery = useQuery<{ tags: TanssTag[]; error?: string }>({
    queryKey: ['dispo-freigabe-tanss-tags'],
    queryFn: async () => {
      const res = await apiClient.get('/dispo/freigabe-settings/tags')
      return res.data
    },
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const saveMutation = useMutation({
    mutationFn: async (settings: FreigabeSettings) => {
      await apiClient.post('/dispo/freigabe-settings', settings)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispo-freigabe-settings'] })
      toast({ title: 'Einstellungen gespeichert', variant: 'success' })
    },
    onError: (err) => {
      toast({ title: 'Fehler beim Speichern', description: extractErrorMessage(err), variant: 'error' })
    },
  })

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState message={extractErrorMessage(error)} />

  const tags: TanssTag[] = tagsQuery.data?.tags ?? []
  const bothCriteriaOff = !form.closedTicketsEnabled && !form.ticketTagRulesEnabled

  const addRule = () => {
    const newRule: TicketTagRule = {
      id: `rule-${Date.now()}`,
      tagIds: [],
      minimumDaysSinceUpdate: 3,
    }
    setForm((f) => ({ ...f, ticketTagRules: [...f.ticketTagRules, newRule] }))
  }

  const removeRule = (id: string) => {
    setForm((f) => ({ ...f, ticketTagRules: f.ticketTagRules.filter((r) => r.id !== id) }))
  }

  const updateRule = (id: string, patch: Partial<TicketTagRule>) => {
    setForm((f) => ({
      ...f,
      ticketTagRules: f.ticketTagRules.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }))
  }

  const handleSave = () => {
    if (form.ticketTagRulesEnabled) {
      const emptyRules = form.ticketTagRules.filter((r) => r.tagIds.length === 0)
      if (emptyRules.length > 0) {
        toast({ title: 'Jede Kombination muss mindestens einen Tag enthalten.', variant: 'error' })
        return
      }
    }
    saveMutation.mutate(form)
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Leistungsfreigabe – Einstellungen"
        icon={<CheckSquare className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Dispo-Einstellungen', href: '/einstellungen/dispo' },
          { label: 'Leistungsfreigabe' },
        ]}
      />

      {/* Ticket-Freigabekriterien */}
      <Card>
        <Card.Header>
          <h2 className="text-h3">Freigabekriterien auf Ticketebene</h2>
          <p className="text-caption text-gray-500 mt-1">
            Legt fest, welche Tickets automatisch zur Freigabe vorgeschlagen werden.
            Mindestens ein Kriterium muss aktiv sein, damit automatische Freigaben stattfinden.
          </p>
        </Card.Header>
        <Card.Body className="space-y-6">
          {/* Abgeschlossene Tickets */}
          <div className="space-y-3">
            <Switch
              checked={form.closedTicketsEnabled}
              onChange={(e) => setForm((f) => ({ ...f, closedTicketsEnabled: e.target.checked }))}
              label="Abgeschlossene Tickets prüfen"
              hint="Tickets werden nach einer konfigurierten Wartezeit nach Abschluss zur Freigabe vorgeschlagen"
            />
            {form.closedTicketsEnabled && (
              <div className="ml-11">
                <Input
                  label="Mindestanzahl Tage nach Ticketabschluss"
                  type="number"
                  min={0}
                  max={3650}
                  value={form.closedTicketsDays}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, closedTicketsDays: Math.max(0, Number(e.target.value)) }))
                  }
                  hint="Tickets werden erst nach dieser Wartezeit zur Freigabe vorgeschlagen (Standard: 3 Tage)"
                />
              </div>
            )}
          </div>

          {/* TANSS-Tag-Regeln */}
          <div className="space-y-3">
            <Switch
              checked={form.ticketTagRulesEnabled}
              onChange={(e) => setForm((f) => ({ ...f, ticketTagRulesEnabled: e.target.checked }))}
              label="Nach TANSS-Tags prüfen"
              hint="Tickets werden anhand konfigurierbarer Tag-Kombinationen zur Freigabe vorgeschlagen"
            />
            {form.ticketTagRulesEnabled && (
              <div className="ml-11 space-y-3">
                {tagsQuery.isLoading ? (
                  <p className="text-caption text-gray-500">TANSS-Tags werden geladen…</p>
                ) : tagsQuery.data?.error ? (
                  <div className="flex items-start gap-2 text-caption text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="space-y-2">
                      <p>{tagsQuery.data.error}</p>
                      <Button variant="ghost" size="xs" onClick={() => tagsQuery.refetch()}>
                        Erneut versuchen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {form.ticketTagRules.length === 0 && (
                      <p className="text-caption text-gray-500 italic">
                        Noch keine Kombination konfiguriert. Füge eine Kombination hinzu.
                      </p>
                    )}
                    {form.ticketTagRules.map((rule, idx) => (
                      <div
                        key={rule.id}
                        className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Kombination {idx + 1}</span>
                          <IconButton
                            icon={<X className="w-4 h-4" />}
                            aria-label="Kombination entfernen"
                            onClick={() => removeRule(rule.id)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            TANSS-Tags{' '}
                            <span className="text-caption text-gray-500 font-normal">
                              (alle müssen vorhanden sein)
                            </span>
                          </label>
                          <TagMultiSelect
                            tagIds={rule.tagIds}
                            onChange={(ids) => updateRule(rule.id, { tagIds: ids })}
                            tags={tags}
                          />
                          {rule.tagIds.length === 0 && (
                            <p className="text-caption text-red-500 mt-1">Mindestens einen Tag auswählen</p>
                          )}
                        </div>
                        <div className="w-64">
                          <Input
                            label="Mindestanzahl Tage seit letzter Änderung"
                            type="number"
                            min={0}
                            max={3650}
                            value={rule.minimumDaysSinceUpdate}
                            onChange={(e) =>
                              updateRule(rule.id, {
                                minimumDaysSinceUpdate: Math.max(0, Number(e.target.value)),
                              })
                            }
                            hint="0 = kein Limit (Ticket wird sofort vorgeschlagen)"
                          />
                        </div>
                      </div>
                    ))}
                    <Button variant="secondary" size="sm" onClick={addRule}>
                      <Plus className="w-4 h-4 mr-1" />
                      Kombination hinzufügen
                    </Button>
                    {form.ticketTagRules.length > 1 && (
                      <p className="text-caption text-gray-500">
                        Mehrere Kombinationen werden mit ODER verknüpft – eine übereinstimmende Kombination reicht.
                        Innerhalb einer Kombination müssen alle Tags vorhanden sein.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Linkage */}
          {form.closedTicketsEnabled && form.ticketTagRulesEnabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Verknüpfung der Kriterien
              </label>
              <SelectRoot
                value={form.criteriaLogic}
                onValueChange={(val) => setForm((f) => ({ ...f, criteriaLogic: val as 'AND' | 'OR' }))}
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OR">ODER – eines der Kriterien reicht</SelectItem>
                  <SelectItem value="AND">UND – beide Kriterien müssen zutreffen</SelectItem>
                </SelectContent>
              </SelectRoot>
              <p className="text-caption text-gray-500 mt-1">
                {form.criteriaLogic === 'OR'
                  ? 'Ein Ticket wird vorgeschlagen, wenn es abgeschlossen ist ODER eine Tag-Kombination zutrifft.'
                  : 'Ein Ticket wird vorgeschlagen, wenn es abgeschlossen ist UND eine Tag-Kombination zutrifft.'}
              </p>
            </div>
          )}

          {/* Warning: Both disabled */}
          {bothCriteriaOff && (
            <div className="flex items-start gap-2 text-caption text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Beide Freigabekriterien sind deaktiviert. Für diesen Mandanten findet
                keine automatische Freigabe statt.
              </span>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Error checks */}
      <Card>
        <Card.Header>
          <h2 className="text-h3">Fehlerprüfungen</h2>
          <p className="text-caption text-gray-500 mt-1">
            Bestimmt, welche Prüfungen bei der Freigabe ausgeführt werden.
            Schlagen Prüfungen fehl, wird die Freigabe blockiert.
          </p>
        </Card.Header>
        <Card.Body className="space-y-5">
          {/* Zeitabweichung */}
          <div className="space-y-3">
            <Switch
              checked={form.checkTimeDeviation}
              onChange={(e) => setForm((f) => ({ ...f, checkTimeDeviation: e.target.checked }))}
              label="Zeitabweichung prüfen"
              hint="Blockiert Freigabe bei zu starker Abweichung zwischen geschätzter und gebuchter Zeit"
            />
            {form.checkTimeDeviation && (
              <div className="ml-11">
                <Input
                  label="Maximale Abweichung (%)"
                  type="number"
                  min={0}
                  max={100}
                  value={form.checkTimeDeviationPercent}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      checkTimeDeviationPercent: Math.min(100, Math.max(0, Number(e.target.value))),
                    }))
                  }
                  hint="Gilt für Über- und Unterschreitungen. Standard: 20 %"
                />
              </div>
            )}
          </div>

          {/* Fehlende Waren */}
          <Switch
            checked={form.checkMissingMaterial}
            onChange={(e) => setForm((f) => ({ ...f, checkMissingMaterial: e.target.checked }))}
            label="Fehlende Warenpositionen prüfen"
            hint="Blockiert Freigabe, wenn Materialpositionen im Ticket fehlen"
          />

          {/* Abrechenbarkeit */}
          <Switch
            checked={form.checkBillability}
            onChange={(e) => setForm((f) => ({ ...f, checkBillability: e.target.checked }))}
            label="Vollständige Abrechenbarkeit prüfen"
            hint="Blockiert Freigabe, wenn nicht alle Leistungen eines Tickets als abrechenbar markiert sind"
          />

          {/* AI plausibility */}
          <Switch
            checked={form.checkAiPlausibility}
            onChange={(e) => setForm((f) => ({ ...f, checkAiPlausibility: e.target.checked }))}
            label="KI-gestützte Plausibilitätsprüfung"
            hint="Nutzt KI zur Prüfung der inhaltlichen Plausibilität von Ticketbeschreibung und Leistungen"
          />
        </Card.Body>
      </Card>

      {/* AI prompt editor — admin-only, shown when AI check is enabled */}
      {form.checkAiPlausibility && <ClearanceAiPromptSection />}

      {/* Manual override */}
      <Card>
        <Card.Header>
          <h2 className="text-h3">Manuelle Übersteuerung</h2>
        </Card.Header>
        <Card.Body className="space-y-4">
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-md p-4">
            <ShieldCheck className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900">
                Übersteuerungsberechtigung
              </p>
              <p className="text-caption text-blue-800">
                Benutzer mit der Berechtigung{' '}
                <span className="font-mono font-semibold">Leistungsfreigabe – Übersteuerung</span>{' '}
                können blockierte Freigaben manuell übersteuern.
                Eine Übersteuerung ist nur mit Begründungstext möglich.
              </p>
              <p className="text-caption text-blue-800">
                Die Berechtigung wird über Berechtigungsgruppen vergeben.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-caption text-gray-500">
            <Info className="w-4 h-4 shrink-0" />
            <span>
              Jede Übersteuerung wird im Audit-Log protokolliert.
            </span>
          </div>

          <div>
            <Link
              to="/einstellungen"
              className="inline-flex items-center gap-1.5 text-sm text-brand-700 hover:text-brand-900 font-medium"
            >
              Berechtigungsgruppen verwalten (Einstellungen → Berechtigungsgruppen)
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Card.Body>
      </Card>

      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleSave}
          loading={saveMutation.isPending}
        >
          Einstellungen speichern
        </Button>
      </div>
    </div>
  )
}
